// src/utils/websocket.js

export default class SocketService {
    constructor(url) {
        this.url = url;
        this.ws = null;
        this.connectCount = 0;
        this.isReconnecting = false;
        this.manualClose = false;

        // 配置参数
        this.heartbeatInterval = 15000; // 15秒心跳
        this.reconnectInterval = 5000;  // 5秒重连
        this.timer = null;
        this.heartTimer = null;

        // 回调钩子
        this.messageCallback = null;
        this.subscribers = new Set(); // 新功能多订阅独立于旧扫码登录的单回调。


    }

    // 初始化连接
    connect() {
        this.manualClose = false;

        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        try {
            this.ws = new WebSocket(this.url);
            this.initEventHandle();
        } catch (e) {
            this.reconnect();
        }
    }

    initEventHandle() {
        this.ws.onopen = () => {
            console.log("WebSocket 连接成功");
            this.isReconnecting = false;
            this.connectCount = 0;
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            this.startHeartbeat();
        };

        this.ws.onmessage = (event) => {
            let data = null;
            try {
                data = JSON.parse(event.data);
            } catch (e) {
                console.warn("WebSocket 收到非 JSON 消息", event.data);
                if (!this.heartTimer) this.startHeartbeat(); // 非JSON消息不能持续推迟认证心跳。
                return;
            }
            if (data.type === "connect") { // 连接成功
                localStorage.setItem("clientId",data.client_id)
            }
            if (this.messageCallback) {
                this.messageCallback(data);
            }
            this.subscribers.forEach(callback => { try { callback(data); } catch (error) { console.error('WebSocket订阅处理失败', error); } }); // 单个订阅失败不覆盖扫码或其他监听。
            // 业务消息不能推迟心跳；只有定时器缺失时才补建，保持原认证与在线统计。
            if (!this.heartTimer) this.startHeartbeat(); // 业务消息频繁到达时也必须按原节奏发送心跳。
        };

        this.ws.onclose = (event) => {
            this.stopHeartbeat();
            this.ws = null;
            if (this.manualClose) {
                console.warn("WebSocket 连接已主动关闭");
                return;
            }
            console.warn("WebSocket 连接关闭，准备重连...", event && event.code, event && event.reason);
            this.reconnect();
        };

        this.ws.onerror = () => {
            console.error("WebSocket 连接错误");
            if (this.ws) {
                this.ws.close();
            } else {
                this.reconnect();
            }
        };
    }

    // 发送消息
    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        } else {
            console.error("WebSocket 未连接，消息发送失败");
        }
    }

    // 重连机制
    reconnect() {
        if (this.manualClose) return;
        if (this.isReconnecting) return;
        this.isReconnecting = true;

        if (this.timer) clearTimeout(this.timer);

        this.timer = setTimeout(() => {
            console.log(`正在尝试第 ${++this.connectCount} 次重连...`);
            this.connect();
        }, this.reconnectInterval);
    }

    // 心跳检测
    startHeartbeat() {
        if (this.heartTimer) clearTimeout(this.heartTimer);
        const token = localStorage.getItem('token');
        const isGuest = !token;
        // 未登录时用 120 秒间隔发 heartbeat_guest，避免因 token 为空被后端断连导致二维码刷新
        const interval = isGuest ? 120000 : this.heartbeatInterval;
        this.heartTimer = setTimeout(() => {
            if (isGuest) {
                this.send({ type: "heartbeat_guest", platforms: "pc" });
            } else {
                this.send({ type: "heartbeat", data: "ping", platforms: "pc", 'access_token': token });
            }
            this.startHeartbeat();
        }, interval);
    }

    // 强制重连：退出登录时调用，立即断开旧连接并马上重新连接，让 LoginBox 收到新 connect 刷新二维码
    forceReconnect() {
        this.manualClose = true; // 先设为主动关闭，防止 onclose 触发带延迟的 reconnect
        this.isReconnecting = false;
        this.stopHeartbeat();
        if (this.ws) {
            this.ws.close();
        }
        this.ws = null;
        // 重置标志位后立即建立新连接，无需等待 reconnectInterval
        this.manualClose = false;
        this.connect();
    }

    stopHeartbeat() {
        if (this.heartTimer) {
            clearTimeout(this.heartTimer);
            this.heartTimer = null;
        }
    }

    // 设置消息接收回调
    onMessage(callback) {
        this.messageCallback = callback;
    }

    subscribe(callback) {
        this.subscribers.add(callback); // 一个全局连接承载多个互不覆盖的订阅。
        return () => this.subscribers.delete(callback); // 页面卸载时精确删除自己的监听。
    }

    // 主动关闭
    close() {
        this.manualClose = true;
        this.isReconnecting = false;
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.stopHeartbeat();
        if (this.ws) {
            this.ws.close();
        }
    }
}
